import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { HiMinusCircle } from "react-icons/hi";
import { FaMapMarkerAlt } from "react-icons/fa";
import PlacesInput from "./PlacesInput";
import SuggestionDiv from "./MapSuggestions";
import useSuggestionNavigation from "../../hooks/useSuggestionNavigation";
import { deleteStop } from "../../store/actions/bookingActions";
import { useDispatch } from "react-redux";
import { searchOptions } from "../../Utilities/mapUtilities";
import { useAtom } from "jotai";
import { isMapShownAtom } from "../../jotai-store/isMapShow";
// BookingMedium and Counter moved to LocationScreen for centralized rendering

const Places = ({
  stops,
  type,
  pickupLocation,
  dropoffLocation,
  handleUpdateBookingObject,
  title,
  // Optional render props to allow callers to render UI immediately after
  // each input block (pickup / stop / dropoff). This keeps logic intact
  // while allowing the Input -> Mediums -> Counter visual order.
  renderAfterPickup, // () => ReactNode
  renderAfterStop, // (index) => ReactNode
  renderAfterDropoff, // () => ReactNode
  isTruckRequired,
}) => {
  // dispatch redux actions
  const dispatch = useDispatch();
  // state to edit a stop
  const [editStopToggle, seteditStopToggle] = useState({
    status: false,
    address: "",
    index: "",
  });

  const [isMapShown] = useAtom(isMapShownAtom);

  // current stop text
  const [currentStop, setcurrentStop] = useState("");
  // state to add a stop
  const [AddStopToggle, setAddStopToggle] = useState(false);

  const [internalPickupLocation, setInternalPickupLocation] = useState("");
  const [intrnalDropoffLocation, setInternalDropoffLocation] = useState("");
  // local editable inputs for stops so typing doesn't immediately overwrite store until selection
  const [stopInputs, setStopInputs] = useState({});


  const handleEditStopSuccessfully = async (e) => {
    try {
      seteditStopToggle(false);
      const newStopsArray = stops;
      const results = await geocodeByAddress(e);
      const latLng = await getLatLng(results[0]);
      newStopsArray[editStopToggle?.index] = {
        ...newStopsArray[editStopToggle?.index],
        stop: e,
        lat: latLng?.lat,
        long: latLng?.lng,
      };

      handleUpdateBookingObject("stops", newStopsArray, "14.1");
      seteditStopToggle({ status: false, address: "", index: null });
      window.location.reload(false);
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };
  const handleAddStop = async (e) => {
    try {
      if (e) {
        const results = await geocodeByAddress(e);
        const latLng = await getLatLng(results[0]);
        const newStop = {
          id: 0,
          stop: e,
          lat: latLng?.lat,
          long: latLng?.lng,
          personName: "",
          personPhone: "",
          instructions: "",
          stopMedium: "",
          noOfStairs: 1,
          noOfFloors: 1,
        };
        if (stops && Array.isArray(stops) && stops?.length > 0) {
          handleUpdateBookingObject("stops", [...stops, newStop], "14.2");
        } else {
          handleUpdateBookingObject("stops", [newStop], "14.3");
        }
      }
      setcurrentStop("");
      setAddStopToggle(false);
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };



  const handleDeleteStop = (index) => {
    try {
      const stopToDelete = stops?.filter(
        (address, addIndex) => addIndex === index
      );

      handleUpdateBookingObject(
        "stops",
        stops?.filter((address, addIndex) => addIndex !== index),
        "14.4"
      );
      if (stopToDelete?.length > 0) {
        dispatch(deleteStop(stopToDelete[0]?.id));
      }
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  const returnPlaceholderForPickup = () => {
    try {
      return "Pickup location";
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  const returnPlaceholderForDropoff = () => {
    try {
      return "Drop off location";
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  const returnPlaceholderForStop = () => {
    try {
      return "Enter additional location";
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  useEffect(() => {
    try {
      setInternalPickupLocation(pickupLocation);
      setInternalDropoffLocation(dropoffLocation);
      // initialize local stop inputs from stops prop
      const map = {};
      stops?.forEach((s, i) => {
        map[i] = s?.stop ?? "";
      });
      setStopInputs(map);
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  }, [pickupLocation, dropoffLocation]);

  // pickup handlers (wrapped so they can be passed to the hook)
  const handlePickupChange = (e) => {
    try {
      if (!e) {
        handleUpdateBookingObject("pickupLocation", "", "14.5");
        handleUpdateBookingObject("pickupLatitude", "", "14.5.lat");
        handleUpdateBookingObject("pickupLongitude", "", "14.5.lng");
      }
      setInternalPickupLocation(e);
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  const handlePickupSelect = async (e) => {
    try {
      // set textual location immediately
      handleUpdateBookingObject("pickupLocation", e, "14.6");
      setInternalPickupLocation(e);

      // geocode to get lat/lng and persist immediately
      const results = await geocodeByAddress(e);
      const latLng = await getLatLng(results[0]);
      handleUpdateBookingObject("pickupLatitude", latLng?.lat ?? "", "14.6.lat");
      handleUpdateBookingObject("pickupLongitude", latLng?.lng ?? "", "14.6.lng");
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };


  const handleDropoffSelect = async (e) => {
    try {
      handleUpdateBookingObject("dropoffLocation", e, "14.8");
      setInternalDropoffLocation(e);

      const results = await geocodeByAddress(e);
      const latLng = await getLatLng(results[0]);
      handleUpdateBookingObject("dropoffLatitude", latLng?.lat ?? "", "14.8.lat");
      handleUpdateBookingObject("dropoffLongitude", latLng?.lng ?? "", "14.8.lng");
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  // update a local stop input while the user types
  const handleStopInputChange = (val, idx) => {
    try {
      setStopInputs((prev) => ({ ...prev, [idx]: val }));
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  // when user selects a suggestion for a stop, geocode and update the stops array
  const handleStopSelect = async (val, idx) => {
    try {
      const results = await geocodeByAddress(val);
      const latLng = await getLatLng(results[0]);
      const newStopsArray = stops ? [...stops] : [];
      newStopsArray[idx] = {
        ...newStopsArray[idx],
        stop: val,
        lat: latLng?.lat,
        long: latLng?.lng,
      };

      handleUpdateBookingObject("stops", newStopsArray, "14.11");
      setStopInputs((prev) => ({ ...prev, [idx]: val }));
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  return (
    <div className="w-full">
      {/* 
        Places AUTO COMP START
        */}

      {/* PlacesAuto */}
      {/* A React component to build a customized UI for Google Maps Places Autocomplete */}
      {/* pickup address */}
      <div className="text-lg font-semibold mt-[5px] mb-[0px]">
        {title ?? "Trip details"}
      </div>
      <div className={`${!isMapShown ? 'space-y-3' : ''}`}>
        <div className={`bg-transparent ${!isMapShown ? "focus-within:bg-white shadow-sm" : ""} rounded-[10px] transition-colors duration-150`} style={{ padding: isMapShown ? '0' : '10px 15px 1px', margin: isMapShown ? '0' : undefined }}>
          <PlacesAutocomplete
            searchOptions={searchOptions}
            value={internalPickupLocation}
            onChange={handlePickupChange}
            onSelect={handlePickupSelect}
          >
            {({
              getInputProps,
              suggestions,
              getSuggestionItemProps,
              loading,
            }) => (
              <SuggestionAutocompleteRenderer
                getInputProps={getInputProps}
                suggestions={suggestions}
                getSuggestionItemProps={getSuggestionItemProps}
                loading={loading}
                placeholder={isMapShown ? returnPlaceholderForPickup().replace(/^From:\s*/i, "") : returnPlaceholderForPickup()}
                inputValue={internalPickupLocation}
                setInputValue={setInternalPickupLocation}
                onSelect={handlePickupSelect}
                inputClassName="w-full focus:border-[#7876A1] focus:border-1 text-sm text-[#373559] bg-transparent outline-none border-0 whitespace-nowrap overflow-hidden truncate"
                iconNode={
                  <img
                    src="/images/NewUiIcons/LocationPin.svg"
                    alt="Location Pin"
                    className="w-[13.81px] h-[17.41px]"
                  />
                }
                extraBefore={isMapShown ? null : <span>From</span>}
                value={internalPickupLocation}
                onClear={() => {
                  try {
                    handleUpdateBookingObject("pickupLocation", "", "14.5");
                    handleUpdateBookingObject("pickupLatitude", "", "14.5.lat");
                    handleUpdateBookingObject("pickupLongitude", "", "14.5.lng");
                    setInternalPickupLocation("");
                  } catch (error) {
                    Sentry.captureException(error);
                    console.log(error);
                  }
                }}
                ariaLabelClear="Clear pickup"
              />
            )}
          </PlacesAutocomplete>
          {/* Render any UI that should appear directly after the pickup input (eg. Mediums + Counter) */}
          {typeof renderAfterPickup === "function" ? (
            <div className={`${isMapShown ? 'mt-0' : 'mt-2'}`}>{renderAfterPickup()}</div>
          ) : null}
        </div>
        {/* stops to cover */}
        {stops?.map((address, index) => (
          <div key={index} className={`${isMapShown ? '' : 'mb-1'}`}>
            <div className={`bg-transparent ${!isMapShown ? "focus-within:bg-white shadow-sm" : ""} rounded-[10px] transition-colors duration-150`} style={{ padding: isMapShown ? '0' : '10px 15px 1px', margin: isMapShown ? '0' : undefined }}>
              {/* Make each stop editable inline using PlacesAutocomplete so it behaves like other inputs */}
              <PlacesAutocomplete
                searchOptions={searchOptions}
                value={stopInputs[index] ?? address?.stop}
                onChange={(val) => handleStopInputChange(val, index)}
                onSelect={(val) => handleStopSelect(val, index)}
              >
                {({
                  getInputProps,
                  suggestions,
                  getSuggestionItemProps,
                  loading,
                }) => (
                  <SuggestionAutocompleteRenderer
                    getInputProps={getInputProps}
                    suggestions={suggestions}
                    getSuggestionItemProps={getSuggestionItemProps}
                    loading={loading}
                    placeholder={returnPlaceholderForStop()}
                    inputValue={stopInputs[index] ?? address?.stop}
                    setInputValue={(val) => handleStopInputChange(val, index)}
                    onSelect={(val) => handleStopSelect(val, index)}
                    inputClassName="w-full text-sm text-[#373559] bg-transparent outline-none border-0 whitespace-nowrap overflow-hidden truncate"
                    iconNode={
                      <img
                        src="/images/NewUiIcons/LocationPin.svg"
                        alt="Location Pin"
                        className="w-[13.81px] h-[17.41px]"
                      />
                    }
                    extraBefore={isMapShown ? null : <span>{`Stop #${index + 1}`}</span>}
                    value={stopInputs[index] ?? address?.stop}
                    onClear={() => handleDeleteStop(index)}
                    ariaLabelClear={`Remove stop ${index + 1}`}
                    showClearAsButton={true}
                    suggestionContainerClassName={"w-full mt-2 mb-2"}
                    suggestionLineClassName={"w-full"}
                  />
                )}
              </PlacesAutocomplete>
              {/* Render any UI that should appear directly after this stop input (eg. Mediums + Counter) */}
              {typeof renderAfterStop === "function" ? (
                <div className={`${isMapShown ? 'mt-0' : 'mt-2'}`}>{renderAfterStop(index)}</div>
              ) : null}
            </div>
          </div>
        ))}
        {/* Line */}
        {AddStopToggle && (
          <div className={`bg-transparent ${!isMapShown ? "focus-within:bg-white shadow-sm" : ""} rounded-[10px] transition-colors duration-150`} style={{ padding: isMapShown ? '0' : '10px 15px 1px', margin: isMapShown ? '0' : undefined }}>
            <PlacesAutocomplete
              searchOptions={searchOptions}
              value={currentStop}
              onChange={(e) => {
                try {
                  setcurrentStop(e);
                } catch (error) {
                  Sentry.captureException(error);
                  console.log(error);
                }
              }}
              id="placesAutocompleteDropOff"
              onSelect={handleAddStop}
            >
              {({
                getInputProps,
                suggestions,
                getSuggestionItemProps,
                loading,
              }) => (
                <SuggestionAutocompleteRenderer
                  getInputProps={getInputProps}
                  suggestions={suggestions}
                  getSuggestionItemProps={getSuggestionItemProps}
                  loading={loading}
                  placeholder={returnPlaceholderForStop()}
                  inputValue={currentStop}
                  setInputValue={setcurrentStop}
                  onSelect={handleAddStop}
                  inputClassName="w-full text-sm text-[#373559] bg-transparent outline-none border-0 truncate focus:border-6"
                  iconNode={
                    <img
                      src="/images/NewUiIcons/LocationPin.svg"
                      alt="Location Pin"
                      className="w-[13.81px] h-[17.41px]"
                    />
                  }
                  extraBefore={isMapShown ? null : <span>Stop</span>}
                  value={currentStop}
                  onClear={() => setcurrentStop("")}
                  ariaLabelClear="Clear stop input"
                  suggestionContainerClassName={"w-full mt-2 mb-2"}
                  suggestionLineClassName={"w-full"}
                />
              )}
            </PlacesAutocomplete>
          </div>
        )}
        {/* drop off address */}
        {!isTruckRequired ? (
          <></>
        ) : (
          <>
            <div className={`bg-transparent  ${!isMapShown ? "focus-within:bg-white shadow-sm" : ""} rounded-[10px] transition-colors duration-150`} style={{ padding: isMapShown ? '0' : '10px 15px 1px', margin: isMapShown ? '0' : undefined }}>
              <PlacesAutocomplete
                searchOptions={searchOptions}
                value={intrnalDropoffLocation}
                onChange={(e) => {
                  try {
                    if (!e) {
                      handleUpdateBookingObject("dropoffLocation", "", "14.7");
                    }
                    setInternalDropoffLocation(e);
                  } catch (error) {
                    Sentry.captureException(error);
                    console.log(error);
                  }
                }}
                id="placesAutocompleteDropOff"
                onSelect={handleDropoffSelect}
              >
                {({
                  getInputProps,
                  suggestions,
                  getSuggestionItemProps,
                  loading,
                }) => (
                  <SuggestionAutocompleteRenderer
                    getInputProps={getInputProps}
                    suggestions={suggestions}
                    getSuggestionItemProps={getSuggestionItemProps}
                    loading={loading}
                    placeholder={isMapShown ? returnPlaceholderForDropoff().replace(/^To:\s*/i, "") : returnPlaceholderForDropoff()}
                    inputValue={intrnalDropoffLocation}
                    setInputValue={setInternalDropoffLocation}
                    onSelect={handleDropoffSelect}
                    inputClassName="w-full text-sm text-[#373559] bg-transparent outline-none border-0 whitespace-nowrap overflow-hidden truncate"
                    iconNode={
                      <img
                        src="/images/NewUiIcons/Search.svg"
                        alt="Search Icon"
                        className="w-[17.16px] h-[17.16px]"
                      />
                    }
                    extraBefore={isMapShown ? null : <span>To</span>}
                    value={intrnalDropoffLocation}
                    onClear={() => {
                      try {
                        handleUpdateBookingObject("dropoffLocation", "", "14.7");
                        handleUpdateBookingObject("dropoffLatitude", "", "14.7.lat");
                        handleUpdateBookingObject("dropoffLongitude", "", "14.7.lng");
                        setInternalDropoffLocation("");
                      } catch (error) {
                        Sentry.captureException(error);
                        console.log(error);
                      }
                    }}
                    ariaLabelClear="Clear dropoff"
                    containerStyle={{ height: 48 }}
                    suggestionContainerClassName={"w-full mt-2 mb-2"}
                    suggestionLineClassName={"w-full"}
                  />
                )}
              </PlacesAutocomplete>
              {typeof renderAfterDropoff === "function" ? (
                <div className={`${isMapShown ? 'mt-0' : 'mt-2'}`}>{renderAfterDropoff()}</div>
              ) : null}
            </div>
          </>
        )}
      </div>
      {/* Add a stop button */}
      <div
        className={`h-[1px] bg-[#CDCCD7] mt-2 mb-2 ${isMapShown ? "hidden" : ""}`}
      ></div>

      <div
        className={`flex ${isMapShown
          ? "justify-end mt-[10.26px] mr-1"
          : "items-center justify-center border-[#7876A1] gap-2 border-1 rounded-[15px] h-[47px]"
          }`}
      >
        <button
          onClick={() => {
            try {
              setAddStopToggle(!AddStopToggle);
              seteditStopToggle({
                address: "",
                index: "",
                status: false,
              });
            } catch (error) {
              Sentry.captureException(error);
              console.log(error);
            }
          }}
          className={`${isMapShown
            ? "text-[#373559] font-[400]"
            : "text-[#4743BF] font-semibold w-full"
            } flex justify-center items-center gap-2 font-instrument`}
        >
          <span>
            {AddStopToggle ? (
              <HiMinusCircle size={20} className="text-[#7876A1]" />
            ) : (
              <img
                src="/images/NewUiIcons/AddIcon.svg"
                alt="Add Icon"
                width="16.74"
                height="16.74"
              />
            )}
          </span>
          {AddStopToggle ? "Remove" : "Add a"} stop
        </button>
      </div>

      {editStopToggle?.status && (
        <PlacesAutocomplete
          searchOptions={searchOptions}
          value={editStopToggle?.address}
          onChange={(e) => {
            try {
              seteditStopToggle({ ...editStopToggle, address: e });
            } catch (error) {
              Sentry.captureException(error);
              console.log(error);
            }
          }}
          id="placesAutocompleteDropOff"
          onSelect={handleEditStopSuccessfully}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading,
          }) => (
            <SuggestionAutocompleteRenderer
              getInputProps={getInputProps}
              suggestions={suggestions}
              getSuggestionItemProps={getSuggestionItemProps}
              loading={loading}
              placeholder={"Enter additional location"}
              inputValue={editStopToggle?.address}
              setInputValue={(val) =>
                seteditStopToggle({ ...editStopToggle, address: val })
              }
              onSelect={handleEditStopSuccessfully}
              inputClassName="w-full text-sm text-[#373559] bg-transparent outline-none border-0 whitespace-nowrap overflow-hidden truncate"
              iconNode={
                <FaMapMarkerAlt style={{ color: "#6b4a9b", fontSize: 18 }} />
              }
              extraBefore={<span>Stop</span>}
              value={editStopToggle?.address}
              onClear={() =>
                seteditStopToggle({ ...editStopToggle, address: "" })
              }
              ariaLabelClear={"Clear edit stop"}
              suggestionContainerClassName={"w-full mt-2 mb-2"}
              suggestionLineClassName={"w-full"}
            />
          )}
        </PlacesAutocomplete>
      )}

      {/* 
        PLACES AUTO COMP END
         */}
    </div>
  );
};

export default Places;

// A small renderer component used inside each PlacesAutocomplete render prop.
// Declared at module level so React hooks can be used here (rules-of-hooks).
function SuggestionAutocompleteRenderer({
  getInputProps,
  suggestions,
  getSuggestionItemProps,
  loading,
  placeholder,
  inputValue,
  setInputValue,
  onSelect,
  inputClassName,
  iconNode,
  extraBefore = null,
  value,
  onClear,
  ariaLabelClear,
  containerStyle,
  showClearAsButton = true,
  suggestionContainerClassName,
  suggestionLineClassName,
}) {
  const nav = useSuggestionNavigation({
    suggestions,
    onSelect,
    setInputValue,
    inputValue,
  });

  const inputProps = getInputProps({ placeholder: placeholder });
  const combinedOnKeyDown = (e) => {
    if (typeof inputProps.onKeyDown === "function") inputProps.onKeyDown(e);
    nav.onKeyDown(e);
  };

  return (
    <div>
      <PlacesInput
        inputNode={
          <input
            className={`${inputClassName} placeholder:text-[#373559]`}

            {...inputProps}
            onKeyDown={combinedOnKeyDown}
          />
        }
        iconNode={iconNode}
        extraBefore={extraBefore}
        value={value}
        onClear={onClear}
        ariaLabelClear={ariaLabelClear}
        containerStyle={containerStyle}
        showClearAsButton={showClearAsButton}
      />

      <SuggestionDiv
        suggestions={suggestions}
        getSuggestionItemProps={getSuggestionItemProps}
        loading={loading}
        containerClassName={suggestionContainerClassName}
        lineClassName={suggestionLineClassName}
        highlightedIndex={nav.highlightedIndex}
        onHoverChange={nav.onMouseHoverChange}
      />
    </div>
  );
}
